import { useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Phone, MapPin } from "lucide-react";

const FleetyGoTermsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="fleetygo-terms">
      <header className="bg-[#3FA9F5] p-4 flex items-center gap-4 sticky top-0 z-10">
        <button onClick={() => navigate(-1)} className="text-white">
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-white text-lg font-semibold">Terms & Conditions</h1>
      </header>

      <main className="flex-1 px-4 py-6 overflow-y-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">FleetyGo – Terms & Conditions</h1>
        <p className="text-sm text-gray-500 mb-6">Effective Date: 25-12-2025</p>

        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">1. Service Overview</h2>
          <p className="text-gray-600 text-sm">FleetyGo is a hyperlocal package delivery service that enables users to send items up to 20 kg via bike couriers within the city.</p>
        </section>

        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">2. Eligibility</h2>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            <li>You must be 18 years or older to use FleetyGo</li>
            <li>Valid phone number required for verification</li>
            <li>Accurate sender and receiver details must be provided</li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">3. Prohibited Items</h2>
          <p className="text-gray-600 text-sm mb-2">The following items are NOT allowed for delivery:</p>
          <ul className="list-disc list-inside text-red-600 text-sm space-y-1">
            <li>Alcohol and alcoholic beverages</li>
            <li>Illegal substances or drugs</li>
            <li>Weapons and ammunition</li>
            <li>Flammable or explosive materials</li>
            <li>Live animals</li>
            <li>Perishable items without proper packaging</li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">4. Pricing</h2>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            <li>Delivery charge: ₹10 per km</li>
            <li>Minimum charge: ₹30 (for distances up to 3 km)</li>
            <li>Platform fee: ₹20</li>
            <li>GST: 18% on platform fee (₹3.60)</li>
            <li>All charges shown before payment</li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">5. Wait Time Charges</h2>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            <li><strong>Free wait time:</strong> First 10 minutes at pickup and delivery locations</li>
            <li><strong>After 10 minutes:</strong> ₹1 per minute (paid directly to the rider)</li>
            <li>Wait time charges are calculated automatically and added to the final bill</li>
            <li>Total wait time is shown on the order summary</li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">6. Payment</h2>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            <li>Payment is required before rider assignment</li>
            <li>Accepted methods: UPI, Credit/Debit Cards, Net Banking</li>
            <li>Cash on Delivery is NOT available</li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">7. PIN Verification</h2>
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
            <p className="text-sm text-green-800 font-medium">🔐 Secure Package Handling</p>
            <p className="text-sm text-green-700 mt-1">
              PIN verification ensures your package is handled only by the assigned rider.
            </p>
          </div>
          
          <h3 className="text-sm font-medium text-gray-800 mt-3 mb-2">7.1 Pickup PIN</h3>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            <li>A 4-digit pickup PIN is provided after payment</li>
            <li>Share this PIN with the rider when they arrive at pickup</li>
            <li><strong>The rider cannot start the trip without verifying this PIN</strong></li>
            <li>This ensures your package is picked up by the correct rider</li>
          </ul>
          
          <h3 className="text-sm font-medium text-gray-800 mt-3 mb-2">7.2 Delivery PIN</h3>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            <li>The Delivery PIN is <strong>hidden until the rider reaches the delivery location</strong></li>
            <li>Once the rider marks arrival, the PIN is revealed in the customer app</li>
            <li>The receiver must share this PIN with the rider to confirm delivery</li>
            <li>This ensures the package is delivered only at the correct location</li>
            <li>Never share PINs except with the assigned rider</li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">8. Cancellation & Refunds</h2>
          
          <h3 className="text-sm font-medium text-gray-800 mt-3 mb-2">8.1 Cancellation Policy</h3>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            <li><strong>Before rider accepts the ride:</strong> Full refund, no cancellation fee</li>
            <li><strong>After rider accepts/starts for pickup:</strong> ₹30 cancellation fee applies (paid to rider). Rest will be refunded.</li>
            <li><strong>After pickup (trip started):</strong> Cancellation is NOT possible</li>
          </ul>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 my-3">
            <p className="text-sm text-blue-800 font-medium">💡 Cancellation Fee Breakdown</p>
            <p className="text-sm text-blue-700 mt-1">
              The ₹30 cancellation fee compensates the rider for their time and effort when they have already accepted and started traveling to your pickup location. This fee is paid directly to the rider.
            </p>
          </div>
          
          <h3 className="text-sm font-medium text-gray-800 mt-3 mb-2">8.2 Refund Timeline</h3>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            <li>Refunds (after deducting applicable cancellation fees) are processed within 5-7 business days</li>
            <li>Refunds are credited to the original payment method</li>
            <li>If no rider is available or accepts: Full refund automatically</li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">9. Liability & Insurance</h2>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
            <p className="text-sm text-yellow-800 font-medium">⚠️ Important Notice</p>
            <p className="text-sm text-yellow-700 mt-1">
              FleetyGo provides delivery services only and does not insure or guarantee the value or condition of items unless optional protection is purchased.
            </p>
          </div>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            <li>FleetyGo is not liable for prohibited items</li>
            <li>Standard coverage limited to ₹500 per package</li>
            <li>Optional protection available for valuable items</li>
            <li>Claims must be reported within 24 hours of delivery</li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">10. Weight Limits</h2>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            <li>Maximum package weight: 20 kg</li>
            <li>Light packages: Up to 5 kg</li>
            <li>Medium packages: 5-10 kg</li>
            <li>Heavy packages: 10-20 kg</li>
            <li>Rider may refuse packages exceeding 20 kg</li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">11. Dispute Resolution & Grievance</h2>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            <li>Disputes must be raised within 24 hours of delivery completion</li>
            <li>Submit complaints via the app or email to support@fleety.co.in</li>
            <li>Include order number and supporting evidence (photos/videos)</li>
            <li>Resolution timeline: 5-7 business days from complaint submission</li>
            <li>Fleety's decision on disputes shall be final and binding</li>
          </ul>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
            <p className="text-sm text-blue-800 font-medium">📞 Grievance Officer</p>
            <p className="text-sm text-blue-700 mt-1">
              Email: support@fleety.co.in | Phone: +91-98453 31817
            </p>
          </div>
        </section>

        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">12. Data Privacy</h2>
          <p className="text-gray-600 text-sm">
            Your personal data is collected and processed in accordance with our Privacy Policy. 
            By using FleetyGo, you consent to such collection and processing.
          </p>
          <a 
            href="/fleetygo/privacy" 
            className="inline-block mt-2 text-[#3FA9F5] text-sm font-medium hover:underline"
          >
            View Privacy Policy →
          </a>
        </section>

        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">13. Changes to Terms</h2>
          <p className="text-gray-600 text-sm">
            FleetyGo may update these Terms from time to time. Significant changes will be notified through the app. 
            Continued use of FleetyGo after changes constitutes acceptance of the updated Terms.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">14. Account Deletion</h2>
          <p className="text-gray-600 text-sm mb-2">You may request to delete your account at any time through the App:</p>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            <li>Navigate to "My Fleety" menu and select "Delete my account"</li>
            <li>Confirm your deletion request when prompted</li>
            <li>Account deletion requests are processed within 5-7 business days</li>
            <li>You will receive confirmation once your account has been deleted</li>
          </ul>
          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>⚠️ Important:</strong> Account deletion is permanent and cannot be undone. Upon deletion, all your personal data, delivery history, saved addresses, and preferences will be permanently removed.
            </p>
          </div>
        </section>

        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">15. Governing Law</h2>
          <p className="text-gray-600 text-sm">
            These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive 
            jurisdiction of courts in Bengaluru, Karnataka.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">16. Contact</h2>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-gray-600 text-sm">
              <Mail size={16} className="text-[#3FA9F5]" />
              <a href="mailto:support@fleety.co.in" className="text-[#3FA9F5]">support@fleety.co.in</a>
            </div>
            <div className="flex items-center gap-2 text-gray-600 text-sm">
              <Phone size={16} className="text-[#3FA9F5]" />
              <a href="tel:+919845331817" className="text-[#3FA9F5]">+91-98453 31817</a>
            </div>
          </div>
        </section>

        <p className="text-center text-xs text-gray-400 mt-8">© 2025 Fleety. All rights reserved.</p>
      </main>
    </div>
  );
};

export default FleetyGoTermsPage;
